import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import ModalComponent from '../../../components/ModalComponent';
import DropdownSelectComponent from '../../../components/DropdownSelectComponent';
import { AppContext } from '../../../context/AppContextProvider';
import { useGetAllStudentByLicenseMutateAsync } from '../../../services/StudentService/hooks/useMutation';
import { IStudentSimpleProps } from '../../../services/StudentService/types';
import {
  useCreateSchoolShiftAsync,
  useUpdateSchoolShiftAsync,
} from '../../../services/SchoolShift/hooks/useMutation';
import { SchoolShiftFormSchema } from '../../../services/SchoolShift/schemas';
import { getAllStudentsByLicenseAsync } from '../../../services/Recognition/Student';
import { useGetAllStudentsByLicenseMutateAsync } from '../../../services/Recognition/Student/hooks/useMutation';
import { IStudentRecognitionSimpleProps } from '../../../services/Recognition/Student/types';
import { IRecognitionSimpleProps } from '../../../services/Recognition/types';
import { useGetAllRecognitionsByLicenseAsync } from '../../../services/Recognition/hooks/useMutation';
import { useGetAllControllersByLicenseAsync } from '../../../services/Devices/hooks/useMutation';
import { IDeviceSimpleProps } from '../../../services/Devices/types';

export type SchoolShiftFormInput = z.infer<typeof SchoolShiftFormSchema>;

interface SchoolShiftEditionModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  responsibleEdit?: any;
  handleSaveForm?: () => void;
  handleClose?: () => void;
}

const DAYS_OF_WEEK = [
  { id: 'Sunday', name: 'Domingo' },
  { id: 'Monday', name: 'Segunda-feira' },
  { id: 'Tuesday', name: 'Terça-feira' },
  { id: 'Wednesday', name: 'Quarta-feira' },
  { id: 'Thursday', name: 'Quinta-feira' },
  { id: 'Friday', name: 'Sexta-feira' },
  { id: 'Saturday', name: 'Sábado' },
];

const SchoolShiftEditionModal = ({
  isOpen,
  setIsOpen,
  responsibleEdit,
  handleClose,
  handleSaveForm,
}: SchoolShiftEditionModalProps) => {
  const form = useForm<SchoolShiftFormInput>({
    resolver: zodResolver(SchoolShiftFormSchema),
    defaultValues: {
      name: '',
      weeklySchedule: [],
      deviceIds: [],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const {
    fields: weeklyDays,
    append: appendDay,
    remove: removeDay,
  } = useFieldArray({
    control,
    name: 'weeklySchedule',
  });

  const { showSnackbar } = useContext(AppContext);
  const [students, setStudents] = useState<IDeviceSimpleProps[]>([]);

  const { mutate: mutateCreate, isLoading: isLoadingCreate } =
    useCreateSchoolShiftAsync();
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } =
    useUpdateSchoolShiftAsync();
  const { mutateAsync: mutateGetStudents } =
    useGetAllControllersByLicenseAsync();

  useEffect(() => {
    if (responsibleEdit) {
      reset(responsibleEdit);
    }
  }, [responsibleEdit]);

  useEffect(() => {
    mutateGetStudents({}).then(setStudents);
  }, []);
  const toTimeSpan = (t?: string | null) => {
    if (!t) return null;
    // input de <input type="time" /> costuma vir como "HH:mm"
    return t.length === 5 ? `${t}:00` : t; // garante HH:mm:ss
  };

  const normalizePayload = (data: SchoolShiftFormInput) => ({
    ...data,
    weeklySchedule: data.weeklySchedule.map((day) => ({
      ...day,
      timeSlots: day.timeSlots
        .map((slot) => {
          const isEnter = slot.type
            ? slot.type === 'enter'
            : (slot.isEnter ?? null);
          const isExit = slot.type
            ? slot.type === 'exit'
            : (slot.isExit ?? null);

          return {
            start: toTimeSpan(slot.start),
            end: toTimeSpan(slot.end),
            isEnter,
            isExit,
          };
        })
        // mantém apenas slots que tenham ao menos um horário preenchido
        .filter((s) => s.start || s.end),
    })),
  });
  const handleSave = (data: SchoolShiftFormInput) => {
    const payload = normalizePayload(data);

    const onSuccess = () => {
      if (handleSaveForm) handleSaveForm();
      showSnackbar('Turno escolar salvo com sucesso.', 'success');
      reset();
      setIsOpen(false);
    };

    if (payload.id) {
      mutateUpdate({ id: payload.id, data: payload }, { onSuccess });
    } else {
      mutateCreate(
        { data: payload },
        {
          onSuccess,
          onError: () =>
            showSnackbar('Erro ao cadastrar turno escolar.', 'error'),
        },
      );
    }
  };

  const onCloseModal = () => {
    reset();
    setIsOpen(false);
    if (handleClose) handleClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onCloseModal}
      title="Cadastro de Turno"
    >
      <FormProvider {...form}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextFieldComponent
                  label="Nome"
                  value={field.value}
                  onChangeText={field.onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="deviceIds"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DropdownSelectComponent
                  label="Dispositivos"
                  selectedValues={field.value}
                  onSelectedValues={field.onChange}
                  options={students}
                  labelField="name"
                  valueField="id"
                  errorMessage={error?.message}
                />
              )}
            />
          </Grid>

          {weeklyDays.map((day, index) => (
            <Grid item xs={12} key={day.id}>
              <Controller
                name={`weeklySchedule.${index}.weekday`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DropdownSelectComponent
                    label="Dia da Semana"
                    selectedValues={field.value ? [field.value] : []}
                    onSelectedValues={(val) => field.onChange(val[0])}
                    options={DAYS_OF_WEEK}
                    labelField="name"
                    valueField="id"
                    errorMessage={error?.message}
                  />
                )}
              />
              {[0, 1, 2, 3].map((slotIndex) => (
                <Grid container spacing={2} sx={{ mt: 1 }} key={slotIndex}>
                  {/* Tipo do slot: Entrada ou Saída */}
                  <Grid item xs={12} sm={4}>
                    <FormLabel component="legend">{`Tipo ${slotIndex + 1}`}</FormLabel>
                    <Controller
                      name={`weeklySchedule.${index}.timeSlots.${slotIndex}.type`}
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          row
                          value={field.value ?? ''}
                          onChange={(_, val) => {
                            field.onChange(val);
                            // Mantém consistência com os campos que o backend espera
                            form.setValue(
                              `weeklySchedule.${index}.timeSlots.${slotIndex}.isEnter`,
                              val === 'enter',
                            );
                            form.setValue(
                              `weeklySchedule.${index}.timeSlots.${slotIndex}.isExit`,
                              val === 'exit',
                            );
                          }}
                        >
                          <FormControlLabel
                            value="enter"
                            control={<Radio />}
                            label="Entrada"
                          />
                          <FormControlLabel
                            value="exit"
                            control={<Radio />}
                            label="Saída"
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>

                  {/* Início */}
                  <Grid item xs={6} sm={4}>
                    <Controller
                      name={`weeklySchedule.${index}.timeSlots.${slotIndex}.start`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextFieldComponent
                          label={`Início ${slotIndex + 1}`}
                          value={field.value}
                          onChangeText={field.onChange}
                          errorMessage={error?.message}
                          placeholder={'08:00'}
                          slotProps={{
                            input: {
                              placeholder: '08:00',
                              type: 'time',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Fim */}
                  <Grid item xs={6} sm={4}>
                    <Controller
                      name={`weeklySchedule.${index}.timeSlots.${slotIndex}.end`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextFieldComponent
                          label={`Fim ${slotIndex + 1}`}
                          placeholder={'12:00'}
                          value={field.value}
                          onChangeText={field.onChange}
                          errorMessage={error?.message}
                          slotProps={{
                            input: {
                              placeholder: '12:00',
                              type: 'time',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              ))}

              <ButtonComponent
                size="small"
                color="error"
                onClick={() => removeDay(index)}
              >
                Remover Dia
              </ButtonComponent>
            </Grid>
          ))}

          <Grid item xs={12}>
            <ButtonComponent
              onClick={() =>
                appendDay({
                  weekday: '',
                  timeSlots: [
                    { start: null, end: null, type: undefined },
                    { start: null, end: null, type: undefined },
                    { start: null, end: null, type: undefined },
                    { start: null, end: null, type: undefined },
                  ],
                })
              }
            >
              Adicionar Dia
            </ButtonComponent>
          </Grid>

          <Grid item xs={12}>
            <ButtonComponent
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleSubmit(handleSave)}
              loading={isLoadingCreate || isLoadingUpdate}
            >
              Salvar
            </ButtonComponent>
          </Grid>
        </Grid>
      </FormProvider>
    </ModalComponent>
  );
};

export default SchoolShiftEditionModal;
